class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 300);

    const facetForm = this.querySelector('form');
    if (facetForm) {
      facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));
    }

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper && typeof onKeyUpEscape === 'function') {
      facetWrapper.addEventListener('keyup', onKeyUpEscape);
    }
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
    FacetFiltersForm.initMobileSheets();
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove, .ssps-filter-chip').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const gridContainer = document.getElementById('ProductGridContainer');
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');

    if (gridContainer) {
      const collectionEl = gridContainer.querySelector('.collection');
      if (collectionEl) collectionEl.classList.add('loading');
    }

    if (countContainer) {
      countContainer.classList.add('loading');
    }

    if (countContainerDesktop && countContainerDesktop.parentElement) {
      countContainerDesktop.parentElement.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        FacetFiltersForm.updateMobileControls(html);
        FacetFiltersForm.scrollToGrid();
      })
      .catch((error) => {
        console.error('Error filtering products:', error);
        const gridContainer = document.getElementById('ProductGridContainer');
        if (gridContainer) {
          const collectionEl = gridContainer.querySelector('.collection');
          if (collectionEl) collectionEl.classList.remove('loading');
        }
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    FacetFiltersForm.updateMobileControls(html);
    FacetFiltersForm.scrollToGrid();
  }

  static renderProductGridContainer(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const newGrid = parsed.getElementById('ProductGridContainer');
    const currentGrid = document.getElementById('ProductGridContainer');
    if (newGrid && currentGrid) {
      currentGrid.innerHTML = newGrid.innerHTML;
    }
  }

  static renderProductCount(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const sourceCountEl = parsed.getElementById('ProductCount') || parsed.getElementById('ProductCountDesktop');
    if (!sourceCountEl) return;

    const countHTML = sourceCountEl.innerHTML;
    const targetMobile = document.getElementById('ProductCount');
    const targetDesktop = document.getElementById('ProductCountDesktop');

    if (targetMobile) {
      targetMobile.innerHTML = countHTML;
      targetMobile.classList.remove('loading');
    }

    if (targetDesktop) {
      targetDesktop.innerHTML = countHTML;
      if (targetDesktop.parentElement) {
        targetDesktop.parentElement.classList.remove('loading');
      }
    }
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    const facetDetailsElements = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    };
    const facetsToRender = Array.from(facetDetailsElements).filter((element) => !matchesIndex(element));
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      const currentEl = document.querySelector(`.js-filter[data-index="${element.dataset.index}"]`);
      if (currentEl) {
        currentEl.innerHTML = element.innerHTML;
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);

    if (countsToRender && event?.target?.closest('.js-filter')) {
      FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop', '.ssps-active-filters-mobile'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      const currentElement = document.querySelector(selector);
      if (!activeFacetsElement || !currentElement) return;
      currentElement.innerHTML = activeFacetsElement.innerHTML;
      if (activeFacetsElement.classList.contains('hidden')) {
        currentElement.classList.add('hidden');
      } else {
        currentElement.classList.remove('hidden');
      }
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderCounts(source, target) {
    const targetElement = target.querySelector('.facets__selected');
    const sourceElement = source.querySelector('.facets__selected');

    const targetElementAccessibility = target.querySelector('.facets__summary');
    const sourceElementAccessibility = source.querySelector('.facets__summary');

    if (sourceElement && targetElement) {
      targetElement.outerHTML = sourceElement.outerHTML;
    }

    if (targetElementAccessibility && sourceElementAccessibility) {
      targetElementAccessibility.outerHTML = sourceElementAccessibility.outerHTML;
    }
  }

  static updateMobileControls(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');

    // Update Mobile Filter Badge
    const newBadge = parsed.getElementById('MobileFilterBadge');
    const currentBadge = document.getElementById('MobileFilterBadge');
    if (newBadge && currentBadge) {
      currentBadge.textContent = newBadge.textContent;
      if (newBadge.classList.contains('hidden')) {
        currentBadge.classList.add('hidden');
      } else {
        currentBadge.classList.remove('hidden');
      }
    }

    // Update Mobile Sort Label
    const newSortLabel = parsed.getElementById('MobileSortButtonLabel');
    const currentSortLabel = document.getElementById('MobileSortButtonLabel');
    if (newSortLabel && currentSortLabel) {
      currentSortLabel.textContent = newSortLabel.textContent;
    }

    // Update Apply Button Text
    const newApplyBtn = parsed.getElementById('MobileFilterApplyBtn');
    const currentApplyBtn = document.getElementById('MobileFilterApplyBtn');
    if (newApplyBtn && currentApplyBtn) {
      currentApplyBtn.textContent = newApplyBtn.textContent;
    }

    // Update Sort Radios checked state
    const newSortInputs = parsed.querySelectorAll('.ssps-sort-radio-input');
    newSortInputs.forEach((input) => {
      if (input.checked) {
        const matchingCurrent = document.querySelector(`.ssps-sort-radio-input[value="${input.value}"]`);
        if (matchingCurrent) matchingCurrent.checked = true;
      }
    });
  }

  static scrollToGrid() {
    const gridContainer = document.getElementById('ProductGridContainer') || document.getElementById('product-grid');
    if (!gridContainer) return;
    const rect = gridContainer.getBoundingClientRect();
    if (rect.top < -100) {
      window.scrollTo({
        top: window.scrollY + rect.top - 80,
        behavior: 'smooth'
      });
    }
  }

  static updateURLHash(searchParams) {
    const newUrl = `${window.location.pathname}${searchParams ? '?' + searchParams : ''}`;
    history.pushState({ searchParams }, '', newUrl);
  }

  static getSections() {
    const grid = document.getElementById('product-grid');
    return [
      {
        section: grid ? grid.dataset.id : 'product-grid',
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    const isMobile = event.target.closest('form')?.id === 'FacetFiltersFormMobile';

    if (isMobile) {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      sortFilterForms.forEach((form) => {
        if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm' || form.id === 'FacetFilterActiveForm') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url = event.currentTarget.href.indexOf('?') === -1 ? '' : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }

  static initMobileSheets() {
    // Mobile Filter Drawer
    const filterTrigger = document.getElementById('MobileFilterTrigger');
    const filterDrawer = document.getElementById('MobileFilterDrawer');
    const filterClose = document.getElementById('MobileFilterClose');
    const filterBackdrop = document.getElementById('MobileFilterBackdrop');
    const filterApply = document.getElementById('MobileFilterApplyBtn');

    const openFilter = () => {
      if (!filterDrawer) return;
      filterDrawer.classList.add('active');
      document.body.classList.add('ssps-sheet-open');
      filterTrigger?.setAttribute('aria-expanded', 'true');
      if (!window.history.state?.filterDrawerOpen) {
        window.history.pushState({ filterDrawerOpen: true }, '', window.location.href);
      }
    };

    const closeFilter = (fromPopstate = false) => {
      if (!filterDrawer) return;
      filterDrawer.classList.remove('active');
      document.body.classList.remove('ssps-sheet-open');
      filterTrigger?.setAttribute('aria-expanded', 'false');
      if (!fromPopstate && window.history.state?.filterDrawerOpen) {
        window.history.back();
      }
    };

    if (filterTrigger && !filterTrigger.dataset.initialized) {
      filterTrigger.dataset.initialized = 'true';
      filterTrigger.addEventListener('click', (e) => {
        e.preventDefault();
        openFilter();
      });
    }

    [filterClose, filterBackdrop, filterApply].forEach((btn) => {
      if (btn && !btn.dataset.initialized) {
        btn.dataset.initialized = 'true';
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          closeFilter();
        });
      }
    });

    // Mobile Sort Drawer
    const sortTrigger = document.getElementById('MobileSortTrigger');
    const sortDrawer = document.getElementById('MobileSortDrawer');
    const sortClose = document.getElementById('MobileSortClose');
    const sortBackdrop = document.getElementById('MobileSortBackdrop');

    const openSort = () => {
      if (!sortDrawer) return;
      sortDrawer.classList.add('active');
      document.body.classList.add('ssps-sheet-open');
      sortTrigger?.setAttribute('aria-expanded', 'true');
      if (!window.history.state?.sortDrawerOpen) {
        window.history.pushState({ sortDrawerOpen: true }, '', window.location.href);
      }
    };

    const closeSort = (fromPopstate = false) => {
      if (!sortDrawer) return;
      sortDrawer.classList.remove('active');
      document.body.classList.remove('ssps-sheet-open');
      sortTrigger?.setAttribute('aria-expanded', 'false');
      if (!fromPopstate && window.history.state?.sortDrawerOpen) {
        window.history.back();
      }
    };

    if (sortTrigger && !sortTrigger.dataset.initialized) {
      sortTrigger.dataset.initialized = 'true';
      sortTrigger.addEventListener('click', (e) => {
        e.preventDefault();
        openSort();
      });
    }

    [sortClose, sortBackdrop].forEach((btn) => {
      if (btn && !btn.dataset.initialized) {
        btn.dataset.initialized = 'true';
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          closeSort();
        });
      }
    });

    if (!window._ssps_sheets_popstate_bound) {
      window._ssps_sheets_popstate_bound = true;
      window.addEventListener('popstate', () => {
        if (filterDrawer?.classList.contains('active')) {
          closeFilter(true);
        }
        if (sortDrawer?.classList.contains('active')) {
          closeSort(true);
        }
      });
      window.addEventListener('pageshow', () => {
        document.body.classList.remove('ssps-sheet-open');
      });
    }

    // Listen to Mobile Sort Radio selections
    document.querySelectorAll('.ssps-sort-radio-input').forEach((radio) => {
      if (!radio.dataset.initialized) {
        radio.dataset.initialized = 'true';
        radio.addEventListener('change', (e) => {
          const selectedValue = e.target.value;
          closeSort();

          // Sync into both desktop and mobile forms
          const mobileForm = document.getElementById('FacetFiltersFormMobile');
          if (mobileForm) {
            let sortInput = mobileForm.querySelector('input[name="sort_by"]');
            if (!sortInput) {
              sortInput = document.createElement('input');
              sortInput.type = 'hidden';
              sortInput.name = 'sort_by';
              mobileForm.appendChild(sortInput);
            }
            sortInput.value = selectedValue;
            const searchParams = new URLSearchParams(new FormData(mobileForm)).toString();
            FacetFiltersForm.renderPage(searchParams);
          } else {
            const currentParams = new URLSearchParams(window.location.search);
            currentParams.set('sort_by', selectedValue);
            FacetFiltersForm.renderPage(currentParams.toString());
          }
        });
      }
    });

    // Escape key closes open sheet
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        if (filterDrawer?.classList.contains('active')) closeFilter();
        if (sortDrawer?.classList.contains('active')) closeSort();
      }
    });
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);

if (!customElements.get('facet-filters-form')) {
  customElements.define('facet-filters-form', FacetFiltersForm);
}
FacetFiltersForm.setListeners();

// Re-init on section load for Theme Editor
document.addEventListener('shopify:section:load', () => {
  FacetFiltersForm.initMobileSheets();
});

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) =>
      element.addEventListener('change', this.onRangeChange.bind(this))
    );

    this.setMinAndMaxValues();
    this.loadRangePrice();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
    this.loadRangePrice();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput && maxInput.value && minInput) minInput.setAttribute('max', maxInput.value);
    if (minInput && minInput.value && maxInput) maxInput.setAttribute('min', minInput.value);
    if (minInput && minInput.value === '' && maxInput) maxInput.setAttribute('min', 0);
    if (maxInput && maxInput.value === '' && minInput) minInput.setAttribute('max', maxInput.getAttribute('max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }

  loadRangePrice() {
    const parent = this.querySelector('.price__slider--filter');
    const sliderPrice = this.querySelector('.slider-price');
    if (!parent || !sliderPrice) return;
    const rangeS = this.querySelectorAll('input[type=range]');
    const numberS = this.querySelectorAll('input[type=number]');
    const priceMaxMin = {
      minValue: this.querySelector('.price_fiter--min-value'),
      maxValue: this.querySelector('.price_fiter--max-value'),
    };

    if (rangeS.length >= 2 && numberS.length >= 2) {
      rangeS.forEach((el) => {
        el.oninput = function () {
          let slide1 = Math.round(parseFloat(rangeS[0].value));
          let slide2 = Math.round(parseFloat(rangeS[1].value));
          if (slide1 > slide2) {
            [slide1, slide2] = [slide2, slide1];
          }
          numberS[0].value = slide1;
          numberS[1].value = slide2;

          if (priceMaxMin.minValue != null) {
            priceMaxMin.minValue.innerText = slide1;
          }

          if (priceMaxMin.maxValue != null) {
            priceMaxMin.maxValue.innerText = slide2;
          }

          const width = (100 * slide2) / rangeS[1].max - (100 * slide1) / rangeS[0].max;
          const left = (100 * slide1) / rangeS[0].max;
          sliderPrice.style.setProperty('--width', `${width}%`);
          sliderPrice.style.setProperty('--left', `${left}%`);

          numberS[0].dispatchEvent(new Event('input', { bubbles: true }));
          numberS[1].dispatchEvent(new Event('input', { bubbles: true }));
        };
      });

      numberS.forEach((el) => {
        el.oninput = function () {
          const number1 = parseFloat(numberS[0].value);
          const number2 = parseFloat(numberS[1].value);

          if (number1 > number2) {
            const tmp = number1;
            numberS[0].value = number2;
            numberS[1].value = tmp;
          }
          rangeS[0].value = number1;
          rangeS[1].value = number2;
        };
      });
    }
  }
}

if (!customElements.get('price-range')) {
  customElements.define('price-range', PriceRange);
}

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    if (facetLink) {
      facetLink.setAttribute('role', 'button');
      facetLink.addEventListener('click', this.closeFilter.bind(this));
      facetLink.addEventListener('keyup', (event) => {
        event.preventDefault();
        if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
      });
    }
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    if (form) {
      form.onActiveFilterClick(event);
    } else {
      const url = event.currentTarget.href.indexOf('?') === -1 ? '' : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
      FacetFiltersForm.renderPage(url);
    }
  }
}

if (!customElements.get('facet-remove')) {
  customElements.define('facet-remove', FacetRemove);
}

class sortBy extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', this.onSortChange);
    this.onBodyClickSort = this.handleBodyClickSort.bind(this);
  }

  onSortChange(event) {
    event.preventDefault();
    if (event.target.classList.contains('sortby__button') || event.target.closest('.sortby__button')) {
      const parent = this.parentElement.parentElement;
      if (parent) {
        parent.classList.contains('active') ? parent.classList.remove('active') : parent.classList.add('active');
        document.body.addEventListener('click', this.onBodyClickSort);
      }
    }
  }

  handleBodyClickSort(evt) {
    const target = evt.target;
    if (!target.classList.contains('sortby__button') && !target.closest('.sortby__button') && !target.closest('.filter__sort--by-conatiner')) {
      const parent = this.parentElement.parentElement;
      if (parent && parent.classList.contains('active')) {
        parent.classList.remove('active');
      }
      document.body.removeEventListener('click', this.onBodyClickSort);
    }
  }
}

if (!customElements.get('sort-select')) {
  customElements.define('sort-select', sortBy);
}
